import { useEffect } from 'react';
import { View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Circle, Path, Svg } from 'react-native-svg';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useAnimateWhenInView } from '@/core/hooks/use-animate-when-in-view';

import { Text } from '@/ui';

const SIZE = 200;
const STROKE = 16;
const PADDING_ANGLE = 1.75;
const CENTER = SIZE / 2;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const AnimatedPath = Animated.createAnimatedComponent(Path);

type DonutSegment = {
  key: string;
  color: string;
  value: number;
};

type PlacedSegment = DonutSegment & {
  startAngle: number;
  endAngle: number;
};

function pointOnRing(angleDeg: number) {
  const radians = (angleDeg * Math.PI) / 180;

  return {
    x: CENTER + RADIUS * Math.sin(radians),
    y: CENTER - RADIUS * Math.cos(radians),
  };
}

function ringArcPath(startAngle: number, endAngle: number) {
  const sweep = endAngle - startAngle;

  if (sweep >= 359.995) {
    const midAngle = startAngle + 180;
    const start = pointOnRing(startAngle);
    const mid = pointOnRing(midAngle);
    const end = pointOnRing(startAngle + 360);

    return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 1 1 ${mid.x} ${mid.y} A ${RADIUS} ${RADIUS} 0 1 1 ${end.x} ${end.y}`;
  }

  const start = pointOnRing(startAngle);
  const end = pointOnRing(endAngle);
  const largeArc = sweep > 180 ? 1 : 0;

  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function placeSegments(segments: DonutSegment[]): PlacedSegment[] {
  const activeSegments = segments.filter((segment) => segment.value > 0);
  const count = activeSegments.length;

  if (count === 0) {
    return [];
  }

  if (count === 1) {
    return [{ ...activeSegments[0], startAngle: 0, endAngle: 360 }];
  }

  const total = activeSegments.reduce((sum, segment) => sum + segment.value, 0);
  const drawableAngle = 360 - count * PADDING_ANGLE;
  let remainingAngle = drawableAngle;
  let cursor = 0;
  const placed: PlacedSegment[] = [];

  activeSegments.forEach((segment, index) => {
    const segmentAngle =
      index === count - 1
        ? remainingAngle
        : (segment.value / total) * drawableAngle;
    remainingAngle -= segmentAngle;

    placed.push({
      ...segment,
      startAngle: cursor,
      endAngle: cursor + segmentAngle,
    });
    cursor += segmentAngle + PADDING_ANGLE;
  });

  return placed;
}

type DonutChartProps = {
  segments: DonutSegment[];
  centerValue: string;
  centerCaption: string;
};

function DonutArc({
  color,
  startAngle,
  endAngle,
  shouldAnimate,
}: {
  color: string;
  startAngle: number;
  endAngle: number;
  shouldAnimate: boolean;
}) {
  const path = ringArcPath(startAngle, endAngle);
  const length = ((endAngle - startAngle) / 360) * CIRCUMFERENCE;
  const progress = useSharedValue(0);

  useEffect(() => {
    if (!shouldAnimate) {
      progress.value = 0;
      return;
    }

    progress.value = 0;
    progress.value = withTiming(1, {
      duration: 800,
      easing: Easing.out(Easing.cubic),
    });
  }, [length, progress, shouldAnimate]);

  const animatedProps = useAnimatedProps(() => ({
    strokeDasharray: [length * progress.value, CIRCUMFERENCE],
  }));

  return (
    <AnimatedPath
      d={path}
      stroke={color}
      strokeWidth={STROKE}
      strokeLinecap="butt"
      fill="none"
      animatedProps={animatedProps}
    />
  );
}

export function DonutChart({
  segments,
  centerValue,
  centerCaption,
}: DonutChartProps) {
  const { styles, theme } = useStyles(stylesheet);
  const isCompactLayout = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const { ref, isInView, onLayout } = useAnimateWhenInView();
  const shouldAnimate = isCompactLayout ? isInView : true;
  const placedSegments = placeSegments(segments);

  return (
    <View
      ref={isCompactLayout ? ref : undefined}
      onLayout={isCompactLayout ? onLayout : undefined}
      style={styles.wrap}
    >
      <Svg width={SIZE} height={SIZE}>
        {placedSegments.length === 0 ? (
          <Circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            stroke={theme.colors.card.default.borderColor}
            strokeWidth={STROKE}
            fill="none"
          />
        ) : null}
        {placedSegments.map((segment) => (
          <DonutArc
            key={segment.key}
            color={segment.color}
            startAngle={segment.startAngle}
            endAngle={segment.endAngle}
            shouldAnimate={shouldAnimate}
          />
        ))}
      </Svg>
      <View style={styles.center}>
        <Text type="title2">{centerValue}</Text>
        <Text type="caption1" style={styles.caption}>
          {centerCaption}
        </Text>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  wrap: {
    width: SIZE,
    height: SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  center: {
    position: 'absolute',
    alignItems: 'center',
  },
  caption: {
    color: theme.colors.text.secondary,
  },
}));
